import { Badge, Checkbox } from '../../components';
import type { ResultItem } from '../../types/api';
import { PLATFORM_LABEL } from './labels';

export function formatDateTime(iso: string | null): string {
  if (!iso) return 'Sem data';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Sem data';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

export function formatCount(value: number | null): string {
  // `null` significa "o fornecedor não informou" — mostrar 0 seria inventar
  // dado (SPEC-006 RF-M03).
  if (value === null) return '—';
  return new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(value);
}

export function ResultCard({
  result,
  onOpen,
  selected,
  onToggleSelect,
}: {
  result: ResultItem;
  onOpen: (id: string) => void;
  /** Seleção só aparece quando a tela oferece exportação (RF-EU05). */
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const author = result.author.displayName ?? result.author.username ?? 'Autor desconhecido';

  return (
    <article className="rounded-lg border border-border bg-surface p-4 shadow-sm">
      <header className="mb-2 flex flex-wrap items-center gap-2">
        {onToggleSelect && (
          <Checkbox
            // O rótulo diz qual resultado seleciona: numa lista, "selecionar"
            // sem dizer o quê não ajuda quem usa leitor de tela (RNF-EU03).
            label={`Selecionar resultado de ${author}`}
            hideLabel
            checked={selected ?? false}
            onChange={() => onToggleSelect(result.id)}
          />
        )}
        <Badge tone="info">{PLATFORM_LABEL[result.platform]}</Badge>
        <span className="text-sm font-medium text-text">{author}</span>
        {result.author.username && (
          <span className="text-xs text-text-muted">@{result.author.username}</span>
        )}
        <span className="ml-auto text-xs text-text-muted">
          {formatDateTime(result.publishedAt)}
        </span>
      </header>

      <p className="mb-3 whitespace-pre-line text-sm text-text">
        {result.text ?? 'Publicação sem texto.'}
      </p>

      <footer className="flex flex-wrap items-center gap-4 text-xs text-text-muted">
        <span>♥ {formatCount(result.engagement.likes)}</span>
        <span>💬 {formatCount(result.engagement.comments)}</span>
        <span>↗ {formatCount(result.engagement.shares)}</span>
        <span title="Relevância calculada para esta pesquisa">
          Relevância {Math.round(result.relevanceScore * 100)}%
        </span>

        {/* Proveniência do §49, no mesmo texto do painel de detalhe. O arquivo
            exportado sempre teve as colunas; a lista, não. */}
        {result.provider && (
          <span>
            Coletado via <span className="text-text">{result.provider}</span>
            {result.collectedAt && ` em ${formatDateTime(result.collectedAt)}`}
          </span>
        )}

        <div className="ml-auto flex gap-3">
          <button
            type="button"
            onClick={() => onOpen(result.id)}
            className="font-medium text-accent hover:underline"
          >
            Detalhes
          </button>
          <a
            href={result.url}
            target="_blank"
            // `noreferrer` impede que a página de destino saiba de onde veio o
            // clique — é conteúdo de terceiro, não confiável.
            rel="noopener noreferrer"
            className="font-medium text-accent hover:underline"
          >
            Abrir original
          </a>
        </div>
      </footer>
    </article>
  );
}
